/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { createContext, useContext, ReactNode, } from "react";

// --- TIPAGENS ---
export type PropTypesColor = "primary" | "secondary" | "default" | "inherit";
export type ButtonVariant = "text" | "outlined" | "contained";
export type ButtonSize = "small" | "medium" | "large";
export type Orientation = "vertical" | "horizontal";

// --- CONTEXTO ---
// Utilizado para repassar as props do grupo para os botões internos
interface ButtonGroupContextType {
  color?: PropTypesColor;
  disabled?: boolean;
  disableElevation?: boolean;
  disableFocusRipple?: boolean;
  disableRipple?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
  orientation?: Orientation;
}

const ButtonGroupContext = createContext<ButtonGroupContextType | null>(null);

export function useButtonGroupContext() {
  return useContext(ButtonGroupContext);
}

// --- BUTTON GROUP ---
export interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  color?: PropTypesColor;
  disabled?: boolean;
  disableElevation?: boolean;
  disableFocusRipple?: boolean; // Tailwind lida com focus via anéis (rings), então pode ser ignorado ou mapeado customizadamente
  disableRipple?: boolean; // Requereria JS extra para ripples estilo Material; mantido na interface por compatibilidade
  fullWidth?: boolean;
  orientation?: Orientation;
  size?: ButtonSize;
  variant?: ButtonVariant;
}

export default function ButtonGroup({
  children,
  color = "primary",
  disabled = false,
  disableElevation = false,
  disableFocusRipple = false,
  disableRipple = false,
  fullWidth = false,
  orientation = "horizontal",
  size = "medium",
  variant = "outlined",
  className = "",
  ...props
}: ButtonGroupProps): JSX.Element {
  const isHorizontal = orientation === "horizontal";

  // Classes base de layout e largura
  const baseClasses = fullWidth ? "flex w-full" : "inline-flex";
  const directionClasses = isHorizontal ? "flex-row" : "flex-col";

  // Sombra aplicada apenas se for contained e não tiver disableElevation
  const shadowClasses =
    variant === "contained" && !disableElevation && !disabled
      ? "shadow-sm"
      : "";

  // Seletores do Tailwind para gerenciar os cantos arredondados e a sobreposição de bordas dos botões filhos
  const groupingClasses = isHorizontal
    ? `
        [&>*:first-child]:rounded-r-none [&>*:last-child]:rounded-l-none [&>*:not(:first-child):not(:last-child)]:rounded-none
        ${variant === "outlined" ? "[&>*:not(:first-child)]:-ml-[1px]" : ""}
        ${variant === "contained" ? "[&>*:not(:last-child)]:border-r [&>*:not(:last-child)]:border-black/10" : ""}
      `
    : `
        [&>*:first-child]:rounded-b-none [&>*:last-child]:rounded-t-none [&>*:not(:first-child):not(:last-child)]:rounded-none
        ${variant === "outlined" ? "[&>*:not(:first-child)]:-mt-[1px]" : ""}
        ${variant === "contained" ? "[&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-black/10" : ""}
      `;

  const contextValue = {
    color,
    disabled,
    disableElevation,
    disableFocusRipple,
    disableRipple,
    size,
    variant,
    orientation,
  };

  return (
    <ButtonGroupContext.Provider value={contextValue}>
      <div
        role="group"
        className={`${baseClasses} ${directionClasses} ${shadowClasses} ${groupingClasses.replace(/\s+/g, " ").trim()} ${className}`}
        {...props}
      >
        {children}
      </div>
    </ButtonGroupContext.Provider>
  );
}